import React, { useEffect, useRef, useState } from "react";
import {
    NotificationDto,
    NotificationsModalProps
} from "../types/NotificationModalProps";
import { useTranslation } from "react-i18next";
import { OverlayPanel } from "primereact/overlaypanel";
import { Badge } from "primereact/badge";
import { ScrollPanel } from "primereact/scrollpanel";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { NOTIFICATIONS_FILTER_OPTIONS } from "./Constants";
import "./NotificationsModal.css";
import { NotificationCard } from "./NotificationCard";

export const NotificationsModal = (props: NotificationsModalProps) => {
    const { t } = useTranslation("common");
    const notificationsPanel = useRef<OverlayPanel>(null);

    const [userNotifications, setUserNotifications] = useState<
        NotificationDto[]
    >(props.notifications?.filter((e) => e.read === false) ?? []);
    const [showRead, setShowRead] = useState<boolean>(false);
    const [showMarkAllAsRead, setShowMarkAllAsRead] = useState<boolean>(
        props.markAllAsRead !== undefined &&
            (props.notifications?.some((e) => e.read === false) ?? false)
    );

    useEffect(() => {
        if (props.notifications) {
            setUserNotifications(
                props.notifications.filter(
                    (notification) => notification.read === showRead
                )
            );

            if (
                props.notifications.some(
                    (notification) => notification.read === false
                )
            ) {
                setShowMarkAllAsRead(props.markAllAsRead !== undefined);
            }
        }
    }, [props.notifications, showRead]);

    const openIconAndBadge = () => {
        const countUnreadNotifications = props.notifications
            ? props.notifications.filter(
                  (notification) => notification.read === false
              ).length
            : 0;
        let badgeValue = countUnreadNotifications.toString();
        if (countUnreadNotifications > 9) {
            badgeValue = "9+";
        }
        return (
            <i className="pi pi-bell p-overlay-badge">
                {countUnreadNotifications > 0 && (
                    <Badge value={badgeValue} severity="danger" />
                )}
            </i>
        );
    };

    const changeNotificationFilter = (e: any) => {
        setShowRead(e.value);
        if (props.notifications) {
            setUserNotifications(
                props.notifications.filter(
                    (notification) => notification.read === e.value
                )
            );

            setShowMarkAllAsRead(
                e.value === false &&
                    props.markAllAsRead !== undefined &&
                    props.notifications.some((e) => e.read === false)
            );
            const notificationCards =
                document.getElementsByClassName("notification-card");
            notificationCards[0]?.scrollIntoView({
                block: "center",
                inline: "nearest"
            });
        }
    };

    const emptyNotificationsText = (): string => {
        let emptyNotificationsText = "";
        if (showRead) {
            emptyNotificationsText = t("notifications.noReadNotifications");
        } else {
            emptyNotificationsText = t("notifications.noUnreadNotifications");
        }

        return emptyNotificationsText;
    };

    const maxNotificationsText = (): string => {
        let maxNotificationsText = "";
        if (showRead) {
            maxNotificationsText = t("notifications.maxReadNotifications");
        } else {
            maxNotificationsText = t("notifications.maxUnreadNotifications");
        }

        return maxNotificationsText;
    };

    const headerText = (): string => {
        return t("notifications.headerText") ?? "";
    };

    const notificationsBody = () => {
        if (userNotifications.length === 0) {
            return (
                <div>
                    <div
                        id="notifications-empty"
                        className="notifications-user-prompt-container flex justify-content-center align-items-center"
                        style={{ height: "100px" }}
                    >
                        {emptyNotificationsText()}
                    </div>
                </div>
            );
        }

        const sortedNotifications = userNotifications.sort(
            (a, b) => b.dateTime.getTime() - a.dateTime.getTime()
        );

        const notificationsToRender = sortedNotifications.slice(0, 100);

        return (
            <ScrollPanel
                style={{
                    height: userNotifications.length > 3 ? "400px" : "auto"
                }}
            >
                <div>
                    {notificationsToRender.map((notification) => {
                        return (
                            <NotificationCard
                                key={notification.id}
                                notification={notification}
                            />
                        );
                    })}
                </div>
                {userNotifications.length > 100 && (
                    <div
                        id="max-notificaitons"
                        className="notifications-user-prompt-container p-4 flex justify-content-center align-items-center text-center"
                    >
                        {maxNotificationsText()}
                    </div>
                )}
            </ScrollPanel>
        );
    };

    const markAllAsRead = (e: any) => {
        e.preventDefault();
        if (props.markAllAsRead) {
            props.markAllAsRead(userNotifications);
            setShowMarkAllAsRead(false);
        }
    };

    const getMarkAllAsReadLabel = () => {
        return t("notifications.markAllAsRead");
    };

    return (
        <div>
            <Button
                onClick={(e) => notificationsPanel.current?.toggle(e)}
                id="notifications-menu-button"
                className="p-button-text"
                icon={openIconAndBadge}
                data-cy="notifications-menu-button"
                loading={props.loading}
            />
            <OverlayPanel
                ref={notificationsPanel}
                id="notifications-panel"
                className="notifications-overlay-panel"
                data-cy="notifications-panel"
                style={{ width: "425px", maxHeight: "500px" }}
            >
                <div
                    id="notifications-panel-header"
                    data-cy="notifications-panel-header"
                    className="flex flex-column notifications-panel-header pb-3"
                >
                    <div className="flex align-items-center justify-content-between">
                        <h3 className="font-semibold">{headerText()}</h3>
                        <div className="">
                            <SelectButton
                                className="notifications-filter-button"
                                value={showRead}
                                onChange={changeNotificationFilter}
                                options={NOTIFICATIONS_FILTER_OPTIONS}
                                unselectable={false}
                                style={{ fontSize: "14px" }}
                            />
                        </div>
                    </div>
                    {!showRead && showMarkAllAsRead && (
                        <div className="flex justify-content-end mark-all-as-read">
                            <Button
                                className="p-button-text text-button"
                                label={getMarkAllAsReadLabel()}
                                onClick={markAllAsRead}
                            />
                        </div>
                    )}
                </div>
                <div
                    id="notifications-panel-body"
                    data-cy="notifications-panel-body"
                    className="notifications-panel-body"
                >
                    <div style={{ width: "100%" }}>{notificationsBody()}</div>
                </div>
            </OverlayPanel>
        </div>
    );
};
